/**
 * Project Initiation Dialog — W3-A · B7 · Workflow-first.
 *
 * The dialog opens a workflow REQUEST — it does NOT create an
 * Active Project directly.  Submission returns HTTP 202 Accepted
 * with the pending workflow instance handle.  The Active Project
 * is created only when an authorised approver approves the request
 * (or the emergency-override path is invoked separately).
 *
 * Backend contract (post-B5):
 *   POST /api/projects/engagement
 *     Idempotency-Key: <client-generated>
 *     body: { project_type, name, client_id|prospect_id, organisational_context, ... }
 *   → HTTP 202 Accepted
 *   {
 *     workflow_instance_id, workflow_code: "project_initiation",
 *     status: "pending_s1", initiator_user_id, created_at,
 *     correlation_id, next_approval_step
 *   }
 *
 * Project-type picker is the FIRST field.  The rest of the form
 * conditionally renders based on the chosen type, matching the
 * frozen v1.1 architecture § 11:
 *
 *   • Client project   — one of {client, prospect} binding required.
 *                        Optional provenance link (Proposal / PO / AMC /
 *                        Change Request) captured as a Proposal number
 *                        for now (single-link shortcut; the backend
 *                        already accepts `provenance_links[]`).
 *   • Internal project — organisational_context.{business_unit,
 *                        cost_centre, initiative} — all three
 *                        mandatory. No client binding, no provenance.
 *
 * Zero financial fields are collected. Idempotency-Key header is
 * attached to POST so network retries do not produce duplicates.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogDescription,
} from "../../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { toast } from "sonner";


const initialState = {
    projectType: "client",       // 'client' | 'internal'
    name: "",
    // Client-only
    source: "client",            // 'client' | 'prospect'
    clientId: "",
    prospectId: "",
    // Internal-only — UAT hotfix 2026-07-25 · master IDs only.
    businessUnitId: "",
    costCentreId: "",
    initiative: "",
    priority: "normal",
};


export default function ProjectCreateDialog({ open, onClose, onSubmitted }) {
    const { user } = useAuth();
    // W3-A · B7.5 (2026-08-02) — Approver-Completes RBAC refinement.
    // Read caller's caps so the dialog surface adapts to their real
    // read scope.  No broad master-read caps are granted.
    const perms = useMemo(
        () => new Set(user?.permissions || []),
        [user],
    );
    const canReadSales = perms.has("sales:read");
    const canReadMasters = (
        perms.has("bu_master:read") && perms.has("cost_centre_master:read")
    );

    const [state, setState] = useState(initialState);
    const [clients, setClients] = useState([]);
    const [prospects, setProspects] = useState([]);
    // UAT hotfix — org masters.
    const [businessUnits, setBusinessUnits] = useState([]);
    const [costCentres, setCostCentres] = useState([]);
    const [ccLoading, setCcLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const setField = (patch) => setState((s) => ({ ...s, ...patch }));

    useEffect(() => {
        if (!open) return;
        setState(initialState);   // reset every re-open
        setBusinessUnits([]);
        setCostCentres([]);
        // W3-A · B7 · fail-soft loading — initiator-only roles (e.g.
        // hr_user) may lack read caps on some pickers (client:read,
        // sales:read, bu_master:read, cost_centre_master:read).  We
        // load each list independently and silently swallow 403s so
        // the dialog opens cleanly.  Missing lists just render as
        // empty pickers with the standard empty-state placeholder.
        //
        // W3-A · B7.5 (2026-08-02) — additionally SKIP the fetches
        // the caller cannot use at all: prospects require sales:read
        // and BU / CC require the org-master caps.  This avoids
        // silent 403 noise in the browser console and makes the
        // network trace align with the on-screen UI.
        const safeGet = async (url) => {
            try {
                const r = await api.get(url);
                return r.data?.items || r.data || [];
            } catch {
                return [];
            }
        };
        (async () => {
            const [cItems, lItems, buItems] = await Promise.all([
                safeGet("/billing/client/list?limit=200"),
                canReadSales
                    ? safeGet("/leads/list?status=open&limit=200")
                    : Promise.resolve([]),
                canReadMasters
                    ? safeGet("/org/business-unit/list?page_size=200")
                    : Promise.resolve([]),
            ]);
            setClients(cItems);
            setProspects(lItems);
            setBusinessUnits(buItems);
        })();
    }, [open, canReadSales, canReadMasters]);

    // Cascade: re-fetch cost centres whenever the BU selection changes.
    useEffect(() => {
        if (!open || !state.businessUnitId || !canReadMasters) {
            setCostCentres([]);
            return;
        }
        setCcLoading(true);
        api.get(
            `/org/cost-centre/list?business_unit_id=${encodeURIComponent(state.businessUnitId)}&page_size=200`,
        )
            .then((r) => setCostCentres(r.data?.items || []))
            .catch(() => setCostCentres([]))   // fail-soft
            .finally(() => setCcLoading(false));
    }, [open, state.businessUnitId, canReadMasters]);

    // Force-flip to Client binding for callers without sales:read
    // (they cannot see the prospect list).  Runs when the dialog
    // opens and any time the source drifts to `prospect`.
    useEffect(() => {
        if (!canReadSales && state.source === "prospect") {
            setState((s) => ({ ...s, source: "client", prospectId: "" }));
        }
    }, [canReadSales, state.source]);

    const validateClient = () => {
        if (state.source === "client" && !state.clientId) {
            toast.error("Select a client.");
            return false;
        }
        if (state.source === "prospect" && !state.prospectId) {
            toast.error("Select a prospect.");
            return false;
        }
        return true;
    };

    const validateInternal = () => {
        // W3-A · B7.5 — Approver-Completes: callers WITHOUT master-read
        // caps skip the BU/CC picker entirely at initiation.  The
        // authorised approver binds them at approval time.  We still
        // require nothing beyond a valid project name in that case.
        if (!canReadMasters) return true;
        const missing = [];
        if (!state.businessUnitId) missing.push("Business Unit");
        if (!state.costCentreId) missing.push("Cost Centre");
        if (!state.initiative.trim()) missing.push("Initiative");
        if (missing.length) {
            toast.error(`Required: ${missing.join(", ")}.`);
            return false;
        }
        return true;
    };

    const submit = async () => {
        if (state.name.trim().length < 3) {
            return toast.error("Project name must be at least 3 characters.");
        }
        if (state.projectType === "client" && !validateClient()) return;
        if (state.projectType === "internal" && !validateInternal()) return;

        setSaving(true);
        try {
            const idempotencyKey = `epx2-create-${crypto.randomUUID?.() || Date.now()}`;
            const payload = {
                project_type: state.projectType,
                name: state.name.trim(),
                priority: state.priority,
            };
            if (state.projectType === "client") {
                if (state.source === "client") payload.client_id = state.clientId;
                else payload.prospect_id = state.prospectId;
            } else if (canReadMasters) {
                // Initiator has master-read caps — they must supply
                // the full organisational context themselves.
                payload.organisational_context = {
                    business_unit_id: state.businessUnitId,
                    cost_centre_id: state.costCentreId,
                    initiative: state.initiative.trim(),
                };
            }
            // else: context-pending path.  Backend accepts the
            // Internal Project Request without `organisational_context`
            // when the caller genuinely lacks bu_master:read AND
            // cost_centre_master:read; the approver binds later.
            const r = await api.post(
                "/projects/engagement",
                payload,
                { headers: { "Idempotency-Key": idempotencyKey } },
            );
            // W3-A · B7 · endpoint returns 202 Accepted with the
            // pending workflow instance handle.  Hand the response
            // up so the parent surface can render
            // RequestSubmittedDialog with the workflow reference,
            // next-steps guidance, and a link to My Requests.
            //
            // NOTE: no engagement_id / engagement_number is present
            // in the 202 payload — the Active Project only exists
            // once an approver acts.
            onSubmitted?.(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const isClient = state.projectType === "client";

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? null : onClose?.())}>
            <DialogContent className="max-w-lg" data-testid="project-create-dialog">
                <DialogHeader>
                    <DialogTitle>Initiate Project Request</DialogTitle>
                    <DialogDescription>
                        This opens a Project Request for approval.
                        The Active Project + engagement number are
                        created only after an authorised approver
                        approves the request. Pick a project type
                        first — the form adapts to the fields
                        required for that type.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Iter 2 — project-type picker (first field) */}
                    <div>
                        <Label>Project type</Label>
                        <RadioGroup
                            value={state.projectType}
                            onValueChange={(v) => setField({ projectType: v })}
                            className="grid grid-cols-2 gap-3 pt-1"
                            data-testid="project-type-radio-group"
                        >
                            <label
                                className={
                                    "flex items-start gap-2 border rounded-md px-3 py-2 cursor-pointer " +
                                    (isClient
                                        ? "border-emerald-500 bg-emerald-50/50"
                                        : "border-slate-200")
                                }
                                data-testid="project-type-client-option"
                            >
                                <RadioGroupItem value="client" className="mt-0.5" data-testid="project-type-client-radio" />
                                <div>
                                    <div className="text-sm font-medium text-slate-900">Client</div>
                                    <div className="text-xs text-slate-500">
                                        Bound to an existing client or open prospect. Optional provenance to Commercial.
                                    </div>
                                </div>
                            </label>
                            <label
                                className={
                                    "flex items-start gap-2 border rounded-md px-3 py-2 cursor-pointer " +
                                    (!isClient
                                        ? "border-emerald-500 bg-emerald-50/50"
                                        : "border-slate-200")
                                }
                                data-testid="project-type-internal-option"
                            >
                                <RadioGroupItem value="internal" className="mt-0.5" data-testid="project-type-internal-radio" />
                                <div>
                                    <div className="text-sm font-medium text-slate-900">Internal</div>
                                    <div className="text-xs text-slate-500">
                                        No client binding — e.g. onboarding programme, internal audit, migration.
                                    </div>
                                </div>
                            </label>
                        </RadioGroup>
                    </div>

                    <div>
                        <Label htmlFor="project-name">Project name</Label>
                        <Input
                            id="project-name"
                            data-testid="project-create-name"
                            value={state.name}
                            onChange={(e) => setField({ name: e.target.value })}
                            placeholder={
                                isClient
                                    ? "e.g. Q3 delivery for Acme Corp"
                                    : "e.g. FY26 employee-onboarding programme"
                            }
                        />
                    </div>

                    {/* Client-only fields */}
                    {isClient ? (
                        <>
                            {/* Binding source picker — Prospect option
                                only rendered for callers with sales:read
                                (they can actually see the leads list).
                                For everyone else the source is silently
                                pinned to Client. */}
                            {canReadSales ? (
                                <div>
                                    <Label>Binding</Label>
                                    <Select
                                        value={state.source}
                                        onValueChange={(v) => setField({ source: v })}
                                    >
                                        <SelectTrigger data-testid="project-create-source-trigger">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="client" data-testid="project-create-source-client">
                                                Client
                                            </SelectItem>
                                            <SelectItem value="prospect" data-testid="project-create-source-prospect">
                                                Open Prospect
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}
                            {state.source === "client" ? (
                                <div>
                                    <Label>Client</Label>
                                    <Select
                                        value={state.clientId}
                                        onValueChange={(v) => setField({ clientId: v })}
                                    >
                                        <SelectTrigger data-testid="project-create-client-trigger">
                                            <SelectValue placeholder="Choose a client..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clients.map((c) => (
                                                <SelectItem key={c.id} value={c.id} data-testid={`project-create-client-option-${c.id}`}>
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : (
                                <div>
                                    <Label>Prospect</Label>
                                    <Select
                                        value={state.prospectId}
                                        onValueChange={(v) => setField({ prospectId: v })}
                                    >
                                        <SelectTrigger data-testid="project-create-prospect-trigger">
                                            <SelectValue placeholder="Choose an open prospect..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {prospects.map((p) => (
                                                <SelectItem key={p.id} value={p.id} data-testid={`project-create-prospect-option-${p.id}`}>
                                                    {p.name || p.lead_number}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </>
                    ) : canReadMasters ? (
                        <>
                            {/* UAT hotfix 2026-07-25 — empty-state readiness banner.
                                Copy per REEDOS final instructions.        */}
                            {businessUnits.length === 0 && (
                                <div
                                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                                    data-testid="project-create-org-masters-empty-banner"
                                >
                                    Organisation masters are not configured.
                                    Configure at least one active Business Unit
                                    and its linked Cost Centre before creating
                                    a project.
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-3">
                                <div>
                                    <Label htmlFor="project-bu">
                                        Business Unit <span className="text-rose-600">*</span>
                                    </Label>
                                    <Select
                                        value={state.businessUnitId}
                                        onValueChange={(v) => setField({
                                            businessUnitId: v,
                                            costCentreId: "", // cascade reset
                                        })}
                                    >
                                        <SelectTrigger
                                            id="project-bu"
                                            data-testid="project-create-business-unit-trigger"
                                        >
                                            <SelectValue placeholder="Choose a Business Unit..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {businessUnits.map((bu) => (
                                                <SelectItem
                                                    key={bu.id}
                                                    value={bu.id}
                                                    data-testid={`project-create-bu-option-${bu.id}`}
                                                >
                                                    {bu.name} ({bu.code})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="project-cc">
                                        Cost Centre <span className="text-rose-600">*</span>
                                    </Label>
                                    <Select
                                        value={state.costCentreId}
                                        onValueChange={(v) => setField({ costCentreId: v })}
                                        disabled={!state.businessUnitId || ccLoading}
                                    >
                                        <SelectTrigger
                                            id="project-cc"
                                            data-testid="project-create-cost-centre-trigger"
                                        >
                                            <SelectValue placeholder={
                                                !state.businessUnitId
                                                    ? "Select a Business Unit first"
                                                    : ccLoading
                                                        ? "Loading..."
                                                        : (costCentres.length === 0
                                                            ? "No Cost Centres for this Business Unit"
                                                            : "Choose a Cost Centre...")
                                            } />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {costCentres.map((cc) => (
                                                <SelectItem
                                                    key={cc.id}
                                                    value={cc.id}
                                                    data-testid={`project-create-cc-option-${cc.id}`}
                                                >
                                                    {cc.name} ({cc.code})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="project-initiative">
                                        Initiative <span className="text-rose-600">*</span>
                                    </Label>
                                    <Input
                                        id="project-initiative"
                                        data-testid="project-create-initiative"
                                        value={state.initiative}
                                        onChange={(e) => setField({ initiative: e.target.value })}
                                        placeholder="e.g. FY26 onboarding automation"
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        // W3-A · B7.5 · Approver-Completes surface.
                        // Caller lacks bu_master:read + cost_centre_master:read
                        // — we hide the BU / CC / Initiative pickers and
                        // show a friendly note.  The authorised approver
                        // binds the finance-side context at approval time.
                        <div
                            className="rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900 space-y-1"
                            data-testid="project-create-approver-completes-note"
                        >
                            <div className="font-medium">
                                Your finance team will assign the Business
                                Unit and Cost Centre during approval.
                            </div>
                            <div className="text-xs text-sky-800">
                                Submit the request with just a clear
                                project name; the reviewer will bind the
                                correct chart-of-accounts placement
                                before approving.
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>Priority</Label>
                        <Select
                            value={state.priority}
                            onValueChange={(v) => setField({ priority: v })}
                        >
                            <SelectTrigger data-testid="project-create-priority-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="project-create-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={
                            saving
                            || state.name.trim().length < 3
                            || (state.projectType === "internal" && canReadMasters && (
                                !state.businessUnitId
                                || !state.costCentreId
                                || !state.initiative.trim()
                            ))
                            || (state.projectType === "client"
                                && state.source === "client"
                                && !state.clientId)
                            || (state.projectType === "client"
                                && state.source === "prospect"
                                && !state.prospectId)
                        }
                        data-testid="project-create-submit"
                    >
                        {saving ? "Submitting..." : "Submit for Approval"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
